import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Eye, KeyRound, MailPlus, ShieldCheck, UserRound, Users } from "lucide-react";
import { api, formatApiError, type EffectiveAccess, type Hotel, type Location, type LocationType, type PermissionMatrix, type User, type UserInvitation } from "../api/client";

const roles = [
  ["hotel_admin", "Hotel admin"],
  ["hotel_director", "Direktor objekta"],
  ["hotel_manager", "Menadzer"],
  ["survey_manager", "Survey manager"],
  ["location_manager", "Menadzer lokacije"],
  ["it_marketing", "IT/Marketing"],
  ["analyst", "Analiticar"],
  ["auditor", "Auditor"],
  ["privacy_officer", "Privacy officer"],
  ["staff_readonly", "Read-only"]
] as const;

type RoleDraft = {
  role: string;
  hotelId?: string;
  locationId?: string;
  locationTypeId?: string;
  permissions?: string[];
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const [actionPassword, setActionPassword] = useState("");
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [invitePreviewRoles, setInvitePreviewRoles] = useState<RoleDraft[]>([{ role: roles[0][0] }]);
  const [createPreviewRoles, setCreatePreviewRoles] = useState<RoleDraft[]>([{ role: roles[0][0] }]);
  const [accessUserId, setAccessUserId] = useState("");
  const [lastInviteUrl, setLastInviteUrl] = useState("");
  const [lastResetUrl, setLastResetUrl] = useState("");
  const users = useQuery({ queryKey: ["users"], queryFn: () => api<User[]>("/api/admin/users") });
  const invitations = useQuery({ queryKey: ["user-invitations"], queryFn: () => api<UserInvitation[]>("/api/admin/user-invitations") });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const locations = useQuery({ queryKey: ["locations"], queryFn: () => api<Location[]>("/api/admin/locations") });
  const locationTypes = useQuery({ queryKey: ["location-types"], queryFn: () => api<LocationType[]>("/api/admin/location-types") });
  const permissionMatrix = useQuery({ queryKey: ["permissions"], queryFn: () => api<PermissionMatrix>("/api/admin/permissions") });
  const effectiveAccess = useQuery({
    queryKey: ["effective-access", accessUserId],
    queryFn: () => api<EffectiveAccess>(`/api/admin/users/${accessUserId}/effective-access`),
    enabled: Boolean(accessUserId)
  });
  const scopedLocations = useMemo(() => (
    (locations.data ?? []).filter((location) => !selectedHotelId || location.hotelId === selectedHotelId)
  ), [locations.data, selectedHotelId]);
  const createUser = useMutation({
    mutationFn: (body: unknown) => api<User>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] })
  });
  const updateUser = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<User>(`/api/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] })
  });
  const createInvitation = useMutation({
    mutationFn: (body: unknown) => api<UserInvitation>("/api/admin/user-invitations", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: (invitation) => {
      setLastInviteUrl(invitation.inviteUrl ?? "");
      queryClient.invalidateQueries({ queryKey: ["user-invitations"] });
    }
  });
  const revokeInvitation = useMutation({
    mutationFn: (id: string) => api<UserInvitation>(`/api/admin/user-invitations/${id}/revoke`, {
      method: "POST",
      body: "{}"
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["user-invitations"] })
  });
  const resetPassword = useMutation({
    mutationFn: (id: string) => api<{ resetUrl: string }>(`/api/admin/users/${id}/password-reset`, {
      method: "POST",
      body: JSON.stringify({ currentPassword: actionPassword })
    }),
    onSuccess: (result) => {
      setLastResetUrl(result.resetUrl);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createUser.mutate({
      email: String(form.get("email")),
      name: String(form.get("name")),
      password: String(form.get("password")),
      currentPassword: String(form.get("currentPassword")),
      roles: roleScopesFromForm(form)
    });
    event.currentTarget.reset();
  }

  function submitInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createInvitation.mutate({
      email: String(form.get("email")),
      name: String(form.get("name")),
      currentPassword: String(form.get("currentPassword")),
      expiresInDays: Number(form.get("expiresInDays") || 7),
      roles: roleScopesFromForm(form)
    });
    event.currentTarget.reset();
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card users-hero">
        <div>
          <span className="panel-eyebrow left">Superadmin Access Control</span>
          <h2>Role, permission-i i objekti na jednom mjestu.</h2>
          <p>Svaki nalog dobija najmanji potreban scope, uz jasan preview sta moze i sta ne moze prije cuvanja.</p>
        </div>
        <div className="object-brand-card">
          <Users size={34} />
          <strong>{users.data?.length ?? 0} korisnika</strong>
          <span>{invitations.data?.filter((invite) => invite.status === "pending").length ?? 0} pending pozivnica</span>
        </div>
      </section>

      <section className="superadmin-overview">
        <article className="access-kpi card">
          <span>Organizations</span>
          <strong>1</strong>
          <small>Active tenant context</small>
        </article>
        <article className="access-kpi card">
          <span>Objects</span>
          <strong>{hotels.data?.length ?? 0}</strong>
          <small>Hotels visible in current scope</small>
        </article>
        <article className="access-kpi card">
          <span>Role matrix</span>
          <strong>{Object.keys(permissionMatrix.data?.rolePermissions ?? {}).length}</strong>
          <small>Enterprise roles mapped to permissions</small>
        </article>
        <article className="access-kpi card">
          <span>Permissions</span>
          <strong>{permissionMatrix.data?.permissions.length ?? 0}</strong>
          <small>Granular backend-enforced actions</small>
        </article>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Korisnici</h2>
              <p>Nalozi su odvojeni po objektima kroz role i hotel scope.</p>
            </div>
          </div>
          <label className="compact-form">
            Trenutna lozinka za status/role izmjene
            <input
              type="password"
              value={actionPassword}
              onChange={(event) => setActionPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Obavezno za activate/pause/archive"
            />
          </label>
          {updateUser.error && <p className="form-error">{formatApiError(updateUser.error)}</p>}
          {resetPassword.error && <p className="form-error">{formatApiError(resetPassword.error)}</p>}
          {lastResetUrl && (
            <div className="result-box">
              <strong>Password reset link</strong>
              <p>{lastResetUrl}</p>
              <button type="button" onClick={() => navigator.clipboard.writeText(lastResetUrl)}><Copy size={16} /> Kopiraj reset link</button>
            </div>
          )}
          {effectiveAccess.data && (
            <EffectiveAccessPreview
              title="Effective access za postojeći nalog"
              access={effectiveAccess.data}
              hotels={hotels.data ?? []}
              locations={locations.data ?? []}
              locationTypes={locationTypes.data ?? []}
            />
          )}
          <div className="user-list">
            {(users.data ?? []).map((user) => (
              <article className="user-card" key={user.id}>
                <UserRound size={18} />
                <div>
                  <strong>{user.name}</strong>
                  <span>{user.email}</span>
                  <small>
                    {user.status}{user.passwordResetRequired ? " - mora promijeniti lozinku" : ""} - {user.roles.map((role) => roleLabel(role, hotels.data ?? [], locations.data ?? [], locationTypes.data ?? [])).join(", ")}
                  </small>
                </div>
                <div className="row-actions">
                  <button disabled={!actionPassword || user.status === "active"} onClick={() => updateUser.mutate({ id: user.id, body: { status: "active", currentPassword: actionPassword } })}>Activate</button>
                  <button disabled={!actionPassword || user.status === "paused"} onClick={() => updateUser.mutate({ id: user.id, body: { status: "paused", currentPassword: actionPassword } })}>Pause</button>
                  <button disabled={!actionPassword || user.status === "archived"} onClick={() => updateUser.mutate({ id: user.id, body: { status: "archived", currentPassword: actionPassword } })}>Archive</button>
                  <button disabled={!actionPassword} onClick={() => resetPassword.mutate(user.id)}><KeyRound size={16} /> Reset</button>
                  <button type="button" onClick={() => setAccessUserId(user.id)}><Eye size={16} /> Access</button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="panel card">
          <h2>Pozovi korisnika</h2>
          <form className="compact-form" onSubmit={submitInvite} onChange={(event) => setInvitePreviewRoles(roleScopesFromForm(new FormData(event.currentTarget)))}>
            <input name="name" placeholder="Ime" required />
            <input name="email" type="email" placeholder="Email" required />
            <input name="currentPassword" type="password" autoComplete="current-password" placeholder="Tvoja trenutna lozinka za re-auth" minLength={8} required />
            <select name="role" required>
              {roles.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <ObjectScopeFields hotels={hotels.data ?? []} locations={scopedLocations} locationTypes={locationTypes.data ?? []} permissions={permissionMatrix.data?.permissions ?? []} selectedHotelId={selectedHotelId} onSelectedHotelId={setSelectedHotelId} />
            <input name="expiresInDays" type="number" min={1} max={30} defaultValue={7} />
            <EffectiveAccessPreview
              title="Preview pozivnice"
              draftRoles={invitePreviewRoles}
              permissionMatrix={permissionMatrix.data}
              hotels={hotels.data ?? []}
              locations={locations.data ?? []}
              locationTypes={locationTypes.data ?? []}
            />
            <button type="submit" disabled={createInvitation.isPending}><MailPlus size={16} /> Posalji pozivnicu</button>
          </form>
          {lastInviteUrl && (
            <div className="result-box">
              <strong>Invite link</strong>
              <p>{lastInviteUrl}</p>
              <button type="button" onClick={() => navigator.clipboard.writeText(lastInviteUrl)}><Copy size={16} /> Kopiraj link</button>
            </div>
          )}
          {createInvitation.error && <p className="form-error">{formatApiError(createInvitation.error)}</p>}

          <div className="recipient-list">
            {(invitations.data ?? []).map((invite) => (
              <article className="recipient-card" key={invite.id}>
                <div>
                  <strong>{invite.name}</strong>
                  <span>{invite.email}</span>
                  <small>{invite.status} - {invite.roles?.map((role) => roleLabel(role, hotels.data ?? [], locations.data ?? [], locationTypes.data ?? [])).join(", ") ?? invite.role} - exp {new Date(invite.expiresAt).toLocaleDateString()}</small>
                </div>
                <div className="row-actions">
                  <button disabled={invite.status !== "pending"} onClick={() => revokeInvitation.mutate(invite.id)}>Revoke</button>
                </div>
              </article>
            ))}
          </div>

          <h2>Novi nalog bez invite-a</h2>
          <form className="compact-form" onSubmit={submit} onChange={(event) => setCreatePreviewRoles(roleScopesFromForm(new FormData(event.currentTarget)))}>
            <input name="name" placeholder="Ime" required />
            <input name="email" type="email" placeholder="Email" required />
            <input name="password" type="password" placeholder="Privremena lozinka, min 12 karaktera" minLength={12} required />
            <input name="currentPassword" type="password" autoComplete="current-password" placeholder="Tvoja trenutna lozinka za re-auth" minLength={8} required />
            <select name="role" required>
              {roles.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <ObjectScopeFields hotels={hotels.data ?? []} locations={scopedLocations} locationTypes={locationTypes.data ?? []} permissions={permissionMatrix.data?.permissions ?? []} selectedHotelId={selectedHotelId} onSelectedHotelId={setSelectedHotelId} />
            <EffectiveAccessPreview
              title="Preview novog naloga"
              draftRoles={createPreviewRoles}
              permissionMatrix={permissionMatrix.data}
              hotels={hotels.data ?? []}
              locations={locations.data ?? []}
              locationTypes={locationTypes.data ?? []}
            />
            <button type="submit"><ShieldCheck size={16} /> Kreiraj scoped nalog</button>
          </form>
          {createUser.error && <p className="form-error">{formatApiError(createUser.error)}</p>}
          <p className="form-note">Za hotel_admin, menadzere i IT/marketing naloge izaberi objekat. Lokaciju biraj samo kada nalog treba biti jos uzi.</p>
        </section>
      </div>
    </div>
  );
}

function EffectiveAccessPreview({
  title,
  access,
  draftRoles,
  permissionMatrix,
  hotels,
  locations,
  locationTypes
}: {
  title: string;
  access?: EffectiveAccess;
  draftRoles?: RoleDraft[];
  permissionMatrix?: PermissionMatrix;
  hotels: Hotel[];
  locations: Location[];
  locationTypes: LocationType[];
}) {
  const permissions = access?.permissions ?? permissionsForDraft(draftRoles ?? [], permissionMatrix);
  const scope = access ? scopeFromAccess(access, hotels, locations, locationTypes) : scopeFromDraft(draftRoles ?? [], hotels, locations, locationTypes);
  const can = allowedStatements(permissions, scope);
  const cannot = deniedStatements(permissions);
  const pii = access?.canViewPII ?? piiSummary(permissions);
  const roleLabels = access
    ? access.roles.map((role) => roleLabel(role, hotels, locations, locationTypes, false))
    : (draftRoles ?? []).map((role) => roleLabel(role, hotels, locations, locationTypes));

  return (
    <section className="effective-access-preview">
      <div className="access-preview-header">
        <div>
          <strong>{title}</strong>
          <span>{permissions.length} permission-a / {scope.summary}</span>
        </div>
        <ShieldCheck size={18} />
      </div>
      <div className="access-scope-row">
        {roleLabels.map((label, index) => <span key={`${label}-${index}`}>{label || "Bez scope-a"}</span>)}
      </div>
      <div className="access-columns">
        <div>
          <h3>Moze</h3>
          <ul>
            {can.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
        <div>
          <h3>Ne moze</h3>
          <ul>
            {cannot.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </div>
      <div className="pii-grid">
        <span className={pii.room ? "enabled" : ""}>Room PII</span>
        <span className={pii.contact ? "enabled" : ""}>Contact PII</span>
        <span className={pii.comment ? "enabled" : ""}>Comment PII</span>
        <span className={pii.export ? "enabled" : ""}>PII export</span>
      </div>
    </section>
  );
}

function ObjectScopeFields({
  hotels,
  locations,
  locationTypes,
  permissions,
  selectedHotelId,
  onSelectedHotelId
}: {
  hotels: Hotel[];
  locations: Location[];
  locationTypes: LocationType[];
  permissions: string[];
  selectedHotelId: string;
  onSelectedHotelId: (value: string) => void;
}) {
  return (
    <div className="scope-picker">
      <span>Objekti koje nalog vidi</span>
      <div className="checkbox-grid">
        {hotels.map((hotel) => (
          <label className="check" key={hotel.id}>
            <input type="checkbox" name="hotelIds" value={hotel.id} onChange={(event) => event.currentTarget.checked && onSelectedHotelId(hotel.id)} />
            {hotel.name}
          </label>
        ))}
      </div>
      <select name="selectedHotelId" value={selectedHotelId} onChange={(event) => onSelectedHotelId(event.target.value)}>
        <option value="">Opcionalno suzi jedan objekat na lokaciju</option>
        {hotels.map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
      </select>
      <select name="locationId" disabled={!selectedHotelId}>
        <option value="">Cijeli izabrani objekat</option>
        {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
      </select>
      <select name="locationTypeId" disabled={!selectedHotelId}>
        <option value="">Ili cijeli tip lokacije</option>
        {locationTypes.map((locationType) => <option key={locationType.id} value={locationType.id}>{locationType.name}</option>)}
      </select>
      <PermissionSelector permissions={permissions} />
      <p className="form-note">Ako oznacis vise objekata, korisnik dobija isti tip role za svaki objekat. Lokacija ili tip lokacije se koriste samo kada je oznacen jedan objekat.</p>
    </div>
  );
}

function PermissionSelector({ permissions }: { permissions: string[] }) {
  if (!permissions.length) return null;
  return (
    <label>
      Dodatne eksplicitne dozvole
      <select name="permissions" multiple size={8}>
        {permissions.map((permission) => <option key={permission} value={permission}>{permission}</option>)}
      </select>
      <span className="form-note">Ovo dodaje permission-e preko osnovne role. Backend ne dozvoljava delegiranje permission-a koje trenutni admin nema.</span>
    </label>
  );
}

function roleScopesFromForm(form: FormData) {
  const role = String(form.get("role"));
  const hotelIds = form.getAll("hotelIds").map(String).filter(Boolean);
  const selectedHotelId = String(form.get("selectedHotelId") || "") || undefined;
  const locationId = String(form.get("locationId") || "") || undefined;
  const locationTypeId = locationId ? undefined : String(form.get("locationTypeId") || "") || undefined;
  const permissions = form.getAll("permissions").map(String).filter(Boolean);
  const scopedHotelIds = hotelIds.length ? hotelIds : selectedHotelId ? [selectedHotelId] : [];
  const permissionPatch = permissions.length ? { permissions } : {};
  if (!scopedHotelIds.length) return [{ role, ...permissionPatch }];
  if (scopedHotelIds.length === 1) return [{ role, hotelId: scopedHotelIds[0], locationId, locationTypeId, ...permissionPatch }];
  return scopedHotelIds.map((hotelId) => ({ role, hotelId, ...permissionPatch }));
}

function permissionsForDraft(rolesInput: RoleDraft[], permissionMatrix?: PermissionMatrix) {
  const permissions = new Set<string>();
  for (const role of rolesInput) {
    for (const permission of permissionMatrix?.rolePermissions[role.role] ?? []) permissions.add(permission);
    for (const permission of role.permissions ?? []) permissions.add(permission);
  }
  return [...permissions].sort();
}

function scopeFromAccess(access: EffectiveAccess, hotels: Hotel[], locations: Location[], locationTypes: LocationType[]) {
  if (access.isOrgWide) return { summary: "cijela organizacija" };
  return {
    summary: [
      labelCount(access.hotelIds, hotels, "objekat"),
      labelCount(access.locationIds, locations, "lokacija"),
      labelCount(access.locationTypeIds, locationTypes, "tip lokacije")
    ].filter(Boolean).join(", ") || "bez dodijeljenog scope-a"
  };
}

function scopeFromDraft(draftRoles: RoleDraft[], hotels: Hotel[], locations: Location[], locationTypes: LocationType[]) {
  const hotelIds = [...new Set(draftRoles.map((role) => role.hotelId).filter(Boolean))] as string[];
  const locationIds = [...new Set(draftRoles.map((role) => role.locationId).filter(Boolean))] as string[];
  const locationTypeIds = [...new Set(draftRoles.map((role) => role.locationTypeId).filter(Boolean))] as string[];
  const orgWide = draftRoles.some((role) => !role.hotelId);
  if (orgWide) return { summary: "cijela organizacija" };
  return {
    summary: [
      labelCount(hotelIds, hotels, "objekat"),
      labelCount(locationIds, locations, "lokacija"),
      labelCount(locationTypeIds, locationTypes, "tip lokacije")
    ].filter(Boolean).join(", ") || "bez dodijeljenog scope-a"
  };
}

function labelCount<T extends { id: string; name: string }>(ids: string[], items: T[], fallback: string) {
  if (!ids.length) return "";
  const names = ids.map((id) => items.find((item) => item.id === id)?.name ?? fallback);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

function allowedStatements(permissions: string[], scope: { summary: string }) {
  const set = new Set(permissions);
  const statements = [
    set.has("hotel.view") && `vidjeti ${scope.summary}`,
    set.has("survey.update") && "uredjivati ankete i pitanja",
    set.has("survey.publish") && "publishovati ankete",
    set.has("qr.create") && "kreirati QR kodove",
    set.has("analytics.view") && "vidjeti analitiku u svom scope-u",
    set.has("incident.resolve") && "rjesavati incidente",
    set.has("user.assign_role") && "dodjeljivati role i scope",
    set.has("feature_flags.manage") && "upravljati feature flagovima",
    set.has("audit.view") && "vidjeti audit log"
  ].filter(Boolean) as string[];
  return statements.length ? statements : ["samo osnovni read-only pristup"];
}

function deniedStatements(permissions: string[]) {
  const set = new Set(permissions);
  const statements = [
    !set.has("user.assign_role") && "ne moze mijenjati korisnicke role",
    !set.has("pii.export") && "ne moze exportovati PII",
    !set.has("feature_flags.manage") && "ne moze mijenjati feature flagove",
    !set.has("survey.publish") && "ne moze publishovati ankete",
    !set.has("qr.update") && "ne moze mijenjati QR rute",
    !set.has("audit.export") && "ne moze exportovati audit log"
  ].filter(Boolean) as string[];
  return statements.length ? statements : ["nema standardnih enterprise ogranicenja za ovu rolu"];
}

function piiSummary(permissions: string[]) {
  const set = new Set(permissions);
  return {
    room: set.has("pii.view_room"),
    contact: set.has("pii.view_contact"),
    comment: set.has("pii.view_comment"),
    export: set.has("pii.export")
  };
}

function roleLabel(
  role: { role: string; hotelId?: string | null; locationId?: string | null; locationTypeId?: string | null; permissions?: string[] | null },
  hotels: Hotel[],
  locations: Location[],
  locationTypes: LocationType[],
  showExplicitPermissions = true
) {
  const hotel = hotels.find((item) => item.id === role.hotelId);
  const location = locations.find((item) => item.id === role.locationId);
  const locationType = locationTypes.find((item) => item.id === role.locationTypeId);
  const explicit = showExplicitPermissions && role.permissions?.length ? `${role.permissions.length} extra` : "";
  return [
    role.role,
    hotel?.name,
    location?.name,
    locationType?.name,
    explicit
  ].filter(Boolean).join(" - ");
}
